import React, {useEffect, useState} from 'react'
import {Stores} from '../stores'
import styled from 'styled-components'
import {useLocation, Redirect, Route, RouteProps} from 'react-router-dom'
import {observer} from 'mobx-react'
import {LoaderIcon} from '../elements/LoaderIcon'
import iltalehti from '../../images/iltalehti_logo.svg'
import {ElectionLogo} from '../elements/ElectionLogo'
import {getData} from '../stores/api/data'
import {theme} from '../theme'
import {VAALIKONE_TOKEN_KEY} from '../stores/config'
import LogoutFrontPageLink from './LogoutFrontPageLink/LogoutFrontPageLink'

interface IProps extends RouteProps {
  stores: Stores
  computedMatch?: any
  showHeader?: boolean
  logoutInfoText?: string
}

// Add interface for the message configuration
interface LogoutFooterMessage {
  text: string
  showBackToStart?: boolean // Optional, will default to true
}

// Update the messages configuration
const LOGOUT_FOOTER_MESSAGES_BY_PAGE: Record<string, LogoutFooterMessage> = {
  kysymys: {
    text:
      'Vastauksesi tallentuu siirryttäessä seuraavaan kysymykseen. Voit muokata vastauksiasi 21.3. asti.\nHuomioithan, että vaalikone avautuu yleisölle jo 14.3.'
  },
  vastaukset: {
    text: 'Voit muokata vastauksiasi 21.3. asti.\nHuomioithan, että vaalikone avautuu yleisölle jo 14.3.'
  },
  kiitos: {
    text: ''
  },
  default: {
    text: 'Voit muokata tietojasi, kuvaasi ja vastauksiasi 21.3. asti.\nHuomioithan, että vaalikone avautuu yleisölle jo 14.3.',
    showBackToStart: false // Only specify where you want to override the default
  }
} as const

const PrivateRoute: React.FC<IProps> = observer(({component: Component, ...props}) => {
  const [loading, setLoading] = useState(true)
  const {search} = useLocation()
  const {authStore, constituencyStore, answerStore, partyStore, candidateStore, questionStore} = props.stores

  const location = useLocation()

  // Replace the existing getFooterInfo function with:
  const getLogoutFooterInfo = () => {
    const candidateId = props.computedMatch.params.candidateId
    const pathname = location.pathname
    const route = Object.keys(LOGOUT_FOOTER_MESSAGES_BY_PAGE).find(key => pathname.includes(key))

    const messageConfig = route
      ? LOGOUT_FOOTER_MESSAGES_BY_PAGE[route as keyof typeof LOGOUT_FOOTER_MESSAGES_BY_PAGE]
      : LOGOUT_FOOTER_MESSAGES_BY_PAGE.default

    const {text, showBackToStart} = messageConfig

    return {
      text,
      ...(candidateId && {candidateId}),
      showBackToStart: showBackToStart ?? true
    }
  }

  useEffect(() => {
    window.scrollTo(0, 0)
  })

  const getAdminToken = () => {
    const searchParams = new URLSearchParams(search)
    return searchParams.get('admin_token')
  }

  const validateAndGetSessionData = () => {
    const electionId = sessionStorage.getItem('election_id')
    const partyId = sessionStorage.getItem('party_id')

    if (!electionId || !partyId) {
      throw new Error('Session data not found')
    }

    return {electionId, partyId}
  }

  const handleGetData = async (electionId: number, candidateId: number | string) => {
    const data = await getData(electionId, candidateId)
    const {constituencies = [], answers = [], parties = [], questions = []} = data

    constituencyStore.setConstituencies(constituencies)
    answerStore.setAnswers(answers)
    partyStore.setParties(parties)
    questionStore.setQuestions(questions)
  }

  useEffect(() => {
    ;(async () => {
      try {
        const isAdmin = search?.includes('admin_token')
        const {candidateId} = props.computedMatch.params

        if (isAdmin) {
          const token = sessionStorage.getItem(VAALIKONE_TOKEN_KEY) || getAdminToken()
          authStore.setToken(token)
          await candidateStore.adminGetCandidate(candidateId)
        } else {
          authStore.setupTunnusTokenRefreshInterceptor()
          const {electionId} = validateAndGetSessionData()
          await candidateStore.fetchAndSetCandidate(candidateId, electionId)
        }

        const validCandidateId = candidateStore?.candidate?.tunnus_id || candidateStore?.candidate?.id

        if (!validCandidateId || !candidateStore?.candidate?.election_id) {
          throw new Error('Candidate missing')
        }

        await handleGetData(candidateStore.candidate.election_id, validCandidateId)
        authStore.setStatus('logged_in')
      } catch (error) {
        authStore.setStatus('unauthorized')
        sessionStorage.removeItem(VAALIKONE_TOKEN_KEY)
        console.error(error) // eslint-disable-line no-console
      } finally {
        setLoading(false)
      }
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const {render, ...rest} = props

  return loading ? (
    <LoadContainer>
      <LoaderIcon height={64} color={theme.color.red} />
    </LoadContainer>
  ) : (
    <Route
      {...rest}
      render={routeProps => {
        return props.stores.authStore.isAuthenticated ? (
          Component ? (
            <MainWrapper>
              <MainContainer>
                {props.showHeader && (
                  <HeaderLogoContainer>
                    <img src={iltalehti} />
                  </HeaderLogoContainer>
                )}
                <ElectionLogo />
                <Container>
                  <Component {...routeProps} />
                </Container>
                <LogoutFrontPageLink footerInfo={getLogoutFooterInfo()} />
              </MainContainer>
            </MainWrapper>
          ) : (
            render!(routeProps)
          )
        ) : (
          <Redirect to="/kirjaudu-ulos" />
        )
      }}
    />
  )
})

export default PrivateRoute

const LoadContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100vh;
`

const MainWrapper = styled.div`
  background-color: ${theme.color.background};
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`

const MainContainer = styled.div`
  background-color: ${theme.color.white};
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 950px;
`

const Container = styled.div`
  background-color: ${theme.color.contentBackground};
  width: 100%;

  @media screen and (min-width: ${theme.breakPoints.mobileMax + 1}px) {
    width: 95%;
  }
`

const HeaderLogoContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: center;
  align-items: center;
  padding: 2rem 0 2rem 0;
  img {
    margin: 0 15px;
  }
  background-color: ${theme.color.background};
`
